import React from 'react'
import { render } from 'react-dom'
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles'
import { teal,cyan } from '@material-ui/core/colors'
import CssBaseline from "@material-ui/core/CssBaseline";
import App from './App'
import {BrowserRouter as Router,Route,Link,useParams,Redirect} from 'react-router-dom'
import {store,persistor} from './reducers';
import { PersistGate } from 'redux-persist/integration/react'

import {Provider} from 'react-redux'
import axios from "axios";
import { installAxiosNetworkLoader, NetworkLoaderOverlay } from "./components/commons/NetworkLoader";
import { bootstrapOfflineMasterData } from "./lib/offlineBootstrap";
import { syncOfflineSalesQueue } from "./lib/offlineSales";

installAxiosNetworkLoader(axios);

// Add global error handler for uncaught axios errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log the error but don't throw it to prevent uncaught errors
    if (error?.message === "Network Error" || !navigator.onLine) {
      console.warn("Network error detected:", error?.message);
    } else if (error?.response?.status >= 500) {
      console.error("Server error:", error?.response?.status, error?.message);
    } else if (error?.response?.status >= 400) {
      console.warn("Request error:", error?.response?.status, error?.message);
    } else {
      console.error("Request error:", error?.message);
    }
    return Promise.reject(error);
  }
);

// Prevent unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason?.message === "Network Error" ||
    event.reason?.isAxiosError ||
    event.reason?.constructor?.name === "AxiosError"
  ) {
    console.warn("Handled unhandled network error:", event.reason?.message);
    event.preventDefault();
  }
});

let offlineBootstrapToken = null;
let offlineBootstrapTimer = null;
let offlineSalesSyncTimer = null;
const isLocalDevelopmentHost =
  typeof window !== "undefined" &&
  /^(localhost|127(?:\.\d{1,3}){3}|0\.0\.0\.0)$/i.test(window.location.hostname);
const shouldEnableServiceWorker =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  false;

const scheduleOfflineBootstrap = (authInfo) => {
  if (typeof window === "undefined" || !authInfo?.token) return;
  if (navigator.onLine === false) return;

  const identity = [
    authInfo?.userInfo?.user_id ?? "",
    authInfo?.userInfo?.branch_id ?? "",
    authInfo?.userInfo?.warehouse_id ?? "",
    authInfo?.token ?? "",
  ].join("|");

  if (offlineBootstrapToken === identity) {
    return;
  }

  offlineBootstrapToken = identity;
  if (offlineBootstrapTimer) {
    clearTimeout(offlineBootstrapTimer);
  }

  offlineBootstrapTimer = setTimeout(() => {
    bootstrapOfflineMasterData(authInfo).catch((error) => {
      console.error("Offline bootstrap failed:", error);
    });
  }, 1200);
};

const scheduleOfflineSalesSync = (authInfo) => {
  if (typeof window === "undefined" || !authInfo?.token) return;
  if (navigator.onLine === false) return;

  if (offlineSalesSyncTimer) {
    clearTimeout(offlineSalesSyncTimer);
  }

  offlineSalesSyncTimer = setTimeout(() => {
    syncOfflineSalesQueue(authInfo).catch((error) => {
      console.error("Offline sales sync failed:", error);
    });
  }, 150);
};

const cleanupDevServiceWorkerCaches = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (_error) {
    // ignore unregister errors in dev
  }

  if (!("caches" in window)) return;

  try {
    const cacheKeys = await caches.keys();
    await Promise.all(
      cacheKeys
        .filter((key) => key.startsWith("app-shell-") || key.startsWith("runtime-"))
        .map((key) => caches.delete(key))
    );
  } catch (_error) {
    // ignore cache cleanup errors in dev
  }
};

if (shouldEnableServiceWorker) {
  navigator.serviceWorker
    .register("/service-worker.js")
    .catch((error) => {
      console.error("Service worker registration failed:", error);
    });

  navigator.serviceWorker.ready
    .then(() => {
      primeOfflineAppShell();
    })
    .catch(() => {
      // ignore readiness errors in dev mode
    });
} else if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  cleanupDevServiceWorkerCaches();
}

const APP_SHELL_CACHE = "app-shell-erp-offline-v6";
const OFFLINE_CACHE_URLS = ["/", "/index.html", "/offline.html", "/static/js/bundle.js", "/static/css/main.css"];

const primeOfflineAppShell = async () => {
  if (!("caches" in window)) return;

  try {
    const cache = await caches.open(APP_SHELL_CACHE);
    const urlsToCache = new Set(OFFLINE_CACHE_URLS);

    document.querySelectorAll("script[src], link[rel='stylesheet'][href], link[rel='icon'][href], link[rel='apple-touch-icon'][href]").forEach((element) => {
      const url = element.getAttribute("src") || element.getAttribute("href");
      if (!url) return;
      try {
        const resolved = new URL(url, window.location.href);
        if (resolved.origin === window.location.origin) {
          urlsToCache.add(resolved.pathname + resolved.search);
        }
      } catch (_error) {
        // ignore invalid asset URLs
      }
    });

    if (window.performance && typeof window.performance.getEntriesByType === "function") {
      window.performance.getEntriesByType("resource").forEach((entry) => {
        try {
          const resolved = new URL(entry.name, window.location.href);
          if (resolved.origin === window.location.origin) {
            urlsToCache.add(resolved.pathname + resolved.search);
          }
        } catch (_error) {
          // ignore invalid resource URLs
        }
      });
    }

    await Promise.all(
      Array.from(urlsToCache).map(async (url) => {
        try {
          const response = await fetch(url, { cache: "no-store" });
          if (response && response.ok) {
            await cache.put(url, response.clone());
          }
        } catch (_error) {
          // ignore individual asset failures so the rest of the shell can still cache
        }
      })
    );
  } catch (_error) {
    // keep the app alive if cache priming fails in dev
  }
};

const theme = createTheme({
  palette: {
    primary: {
      main: teal[600],
      light: teal[50],
      dark:  teal[50],
      contrastText: '#fff'
    },
    secondary: {
      main: teal[600],
      light: teal[50],
      dark:  teal[50],
      contrastText: '#fff'
    },
    type: 'light',
  },
  sidebar:cyan[100],
  topNavApp:{
    bg:cyan[50],
    color:'#000000'
  },
  rootApp:{
    bg:'linear-gradient(to bottom, #33ccff 0%, #ff99cc 100%);'
  }
});

render(
  
  <MuiThemeProvider theme={theme}>
   
    <CssBaseline />
    <Provider store={store}> 
    <PersistGate loading={<NetworkLoaderOverlay message="Restoring session..." />} persistor={persistor}>
   
    <App />
   
    </PersistGate>

    </Provider>
 
  </MuiThemeProvider>
  
,
  document.getElementById('root')
)

const scheduleOfflineShellPrime = () => {
  if (typeof window === "undefined") return;
  if (document.readyState === "complete" || document.readyState === "interactive") {
    setTimeout(() => {
      primeOfflineAppShell();
    }, 0);
  } else {
    window.addEventListener(
      "load",
      () => {
        primeOfflineAppShell();
      },
      { once: true }
    );
  }
};

scheduleOfflineShellPrime();

scheduleOfflineBootstrap(store.getState()?.authInfoReducer);
scheduleOfflineSalesSync(store.getState()?.authInfoReducer);

store.subscribe(() => {
  const authInfo = store.getState()?.authInfoReducer;
  scheduleOfflineBootstrap(authInfo);
  scheduleOfflineSalesSync(authInfo);
});

window.addEventListener("online", () => {
  const authInfo = store.getState()?.authInfoReducer;
  scheduleOfflineBootstrap(authInfo);
  scheduleOfflineSalesSync(authInfo);
});
